<script lang="ts">
import PolicyEditor from './PolicyEditor.vue';

export default {
  name: 'ProgramsLibraryPanel',
  components: {
    PolicyEditor,
  },
};
</script>

<script setup lang="ts">
import { StoreMutationType } from '@/types/StoreMutationType';
import { watch, computed, inject, ref, onMounted, onBeforeMount, ComponentPublicInstance, onBeforeUnmount } from 'vue';
import { ProgramInfo, ProgramPolicy } from '@/types/Programs';
import { programService } from '@/services/api';
import { Type as PolicyRectType } from './objects/policy_rect/PolicyRect';
import { TYPE as CustomPolicyRectType } from './objects/custom_policy_rect/CustomPolicyRect';
import { AppState, Commit } from '@/store';
import { MutationPayload, Store } from 'vuex';
import { ProgramTower } from '@/types/tower/v2/Tower';
import { ProgramPolicyItem, TabType, Tab } from './objects/ProgramsLibraryPanel';
import { compactNumber } from '@/utilities/Number';
import { DateTime } from 'luxon';
import {
  ADD_ALL_POLICIES,
  ADD_CUSTOM_POLICY,
  ADD_POLICY,
  PUBLISH_POLICY,
  REMOVE_CUSTOM_POLICY,
  REMOVE_POLICY,
  SYNC_TOWER_LIBRARY_PANE,
  UPDATE_POLICY_CHILDREN,
} from '@/store/CustomEvents';
import { DisplayOptions, Label } from '@/types/tower/Tower';
import { towerService } from '@/services/v2/TowerService';
import { getElementHeight } from '@/utilities/DOM';
import { useRouter } from 'vue-router';
import { Edit, ChevronRight, ChevronDown, Circle, Trash2, Plus, Pencil, X, CircleCheck, CircleAlert, RefreshCwOff } from 'lucide-vue-next';

import AppModal from '@/components/v2/AppModal.vue';

const router = useRouter();
const subscriptions: ({ (): void } | undefined)[] = [];

// -----
// refs
// -----

const store = inject<Store<AppState>>('store');
const programInfo = ref<ProgramInfo>();
const policies = ref<ProgramPolicyItem[]>([]);
const customPolicies = ref<ProgramPolicyItem[]>([]);
const labels = ref<typeof Label>(Label);
const visibleLabels = ref<DisplayOptions>();
const tabs = ref<Tab[]>([
  {
    title: 'Program',
    ariaLabel: 'Programs Library',
    radioInputValue: 'program',
    labelText: 'Program',
  },
  {
    title: 'Versions',
    ariaLabel: 'Tower Versions',
    radioInputValue: 'versions',
    labelText: 'Versions',
  },
  {
    title: 'Display',
    ariaLabel: 'Display Options',
    radioInputValue: 'display-options',
    labelText: 'Display',
  },
]);
const sidebar = ref<HTMLElement | undefined>(undefined);
const policyCardRefs = new Map<number, Element | ComponentPublicInstance | null>();
const customPolicyCardRefs = new Map<number, Element | ComponentPublicInstance | null>();
const isPoliciesCollapsed = ref<boolean>(false);
const isCustomPoliciesCollapsed = ref<boolean>(false);
const deleteCustomPolicyState = ref<{
  show: boolean;
  index?: number;
}>({ show: false, index: undefined });

// ---------
// computed
// ---------

const show = computed(() => store?.state.towerCanvasState.programsPanelState.show);
const policyEditor = computed(() => {
  const activeCanvasObj = store?.state.towerCanvasState.activeCanvasObject;

  if (!(activeCanvasObj instanceof fabric.PolicyRect)) {
    return {
      show: false,
      expanded: false,
      policy: undefined,
      policyType: '',
      policyRectId: '',
    };
  }

  const policy = activeCanvasObj as fabric.PolicyRect;

  return {
    show: store?.state.towerCanvasState.isTowerEditorVisible,
    expanded: store?.state.towerCanvasState.isTowerEditorExpanded,
    policy: policy.getPolicyData(),
    policyType: policy.type!,
    policyRectId: policy.id,
  };
});
const selectedTab = computed(() => store?.state.towerCanvasState.programsPanelState.tab ?? 'program');
const programId = computed(() => store?.state.towerCanvasState.programId);
// TODO: this needs to be set for new canvas interactions
const currentTowerInstance = computed(() => store?.state.towerCanvasState.currentTowerInstance);
const displayOptions = computed(() => store?.state.towerCanvasState.displayOptions);
const isDeletedProgramMode = computed(() => store?.state.towerCanvasState.isDeletedProgramMode);

const policyCards = computed(() => {
  const activeObj = store?.state.towerCanvasState.activeCanvasObject;
  if (!activeObj || !(activeObj.type === PolicyRectType)) return policies.value;

  const activePolicyId = (activeObj as fabric.PolicyRect).getPolicyData().id;
  const activePolicyLineId = (activeObj as fabric.PolicyRect).getPolicyData().policyLineId;

  return policies.value.map((p) => ({
    ...p,
    active: p.id === activePolicyId && p.policyLineId === activePolicyLineId,
  }));
});

const customPolicyCards = computed(() => {
  const activeObj = store?.state.towerCanvasState.activeCanvasObject;
  if (!activeObj || !(activeObj.type === CustomPolicyRectType)) return customPolicies.value;
  const activePolicyId = (activeObj as fabric.CustomPolicyRect).getPolicyData().id;

  return customPolicies.value.map((p) => ({ ...p, active: p.id === activePolicyId }));
});

// towers filtered by program id
// TODO: this will be updated when new versions are created
const towerVersions = computed(() => {
  let towers = store?.state.towerCanvasState.towerVersions ?? [];
  let currProgramId = programId.value;

  if (towers && currProgramId) {
    towers = towers.filter((e) => e.programId === currProgramId);
  }

  return towers;
});

// ---------
// watchers
// ---------

watch(policyCards, () => {
  const active = policyCards.value?.find((p) => p.active);
  if (!active) return;

  const activeElement = policyCardRefs.get(active.id);
  if (!(activeElement instanceof HTMLElement)) return;

  if (isElementVisible(activeElement)) return;

  sidebar.value?.scrollTo({
    top: activeElement.offsetTop,
    behavior: 'smooth',
  });
});

watch(customPolicyCards, () => {
  const active = customPolicyCards.value?.find((p) => p.active);
  if (!active) return;

  const activeElement = customPolicyCardRefs.get(active.id);
  if (!(activeElement instanceof HTMLElement)) return;

  if (isElementVisible(activeElement)) return;

  sidebar.value?.scrollTo({
    top: activeElement.offsetTop,
    behavior: 'smooth',
  });
});

function isElementVisible(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  const headerHeight = getElementHeight(document.getElementById('header-root'));

  return rect.top >= headerHeight && rect.bottom <= windowHeight && rect.left >= 0 && rect.right <= windowWidth;
}

watch(displayOptions, (val) => {
  visibleLabels.value = val;
});

watch(
  () => programId.value,
  async (val) => {
    if (val) {
      try {
        programInfo.value = await getProgramById(val);
        await syncProgramPoliciesWithCurrentTower();
      } catch {
        programInfo.value = undefined;
        store?.commit(StoreMutationType.SET_DELETED_PROGRAM_MODE, true);
      }
    }
  },
);

// disable editing mode when the policy editor is closed
watch(
  () => policyEditor.value.show,
  () => {
    Commit(StoreMutationType.SET_IS_EDITING, false);
  },
);

// ----------------
// event listeners
// ----------------

addEventListener(SYNC_TOWER_LIBRARY_PANE, handleSyncTowerLibraryPane as EventListener);
addEventListener(PUBLISH_POLICY, handlePublishPolicy as EventListener);
addEventListener(UPDATE_POLICY_CHILDREN, handleUpdatePolicyChildren as EventListener);

// --------
// methods
// --------

function isCurrentTowerVersion(tower: ProgramTower): boolean {
  return currentTowerInstance.value?.towerId === tower.towerId;
}

function editDisplayOptions(): void {
  if (isDeletedProgramMode.value) return;
  store?.commit(StoreMutationType.EDIT_TOWER_DISPLAY_OPTIONS, {
    show: true,
  });
}

// ----------------
// lifecycle hooks
// ----------------

onBeforeMount(() => {
  visibleLabels.value = store?.state.towerCanvasState.displayOptions;
});

onMounted(async () => {
  subscriptions.push(store?.subscribe(handleConfigChange));

  // if a program ID exists, then load the policies for view
  if (programId.value) {
    try {
      programInfo.value = await getProgramById(programId.value);
      // get the canvas policy rect IDs (if they exist)
      await syncProgramPoliciesWithCurrentTower();
    } catch {
      programInfo.value = undefined;
      store?.commit(StoreMutationType.SET_DELETED_PROGRAM_MODE, true);
    }
  }

  await syncCustomProgramPoliciesWithCurrentTower();
});

onBeforeUnmount(() => {
  //unsub from events
  removeEventListener(SYNC_TOWER_LIBRARY_PANE, handleSyncTowerLibraryPane as EventListener);

  subscriptions.forEach((unsubscribe) => {
    if (unsubscribe) {
      unsubscribe();
    }
  });
});

// ---------
// handlers
// ---------

function handleConfigChange(mutation: MutationPayload) {
  if (mutation.type === StoreMutationType.SET_CURRENT_TOWER_INSTANCE) {
    handleSyncTowerLibraryPane();
  }
}

async function handleTowerVersionClick(tower: ProgramTower): Promise<void> {
  if (!tower.loadError) {
    await router.replace(`/v2/tower/${tower.towerId}`);
    location.reload();
  }
}

function handleTabClick(clickedTab: TabType): void {
  store?.commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, { show: true, tab: clickedTab });
}

function updateDisplayOption(e: Event): void {
  if (isDeletedProgramMode.value) return;
  if (!e.target || !(e.target instanceof HTMLInputElement) || !visibleLabels.value) return;

  const target = e.target as HTMLInputElement;
  const option = target.value as keyof DisplayOptions;

  if (!visibleLabels.value[option]) {
    visibleLabels.value[option] = { customName: '', enabled: false };
  }

  let labelValue = visibleLabels.value[option];
  if (labelValue) {
    labelValue.enabled = target.checked;
  }

  store?.commit(StoreMutationType.SET_TOWER_DISPLAY_OPTIONS, visibleLabels.value);
}

function handlePolicyClick(index: number): void {
  if (isDeletedProgramMode.value) return;
  if (!policies.value) return;

  const policy = policies.value[index];
  policy.added = !policy.added;
  const eventType = policy.added ? ADD_POLICY : REMOVE_POLICY;

  dispatchEvent(
    new CustomEvent(eventType, {
      detail: {
        policy,
      },
    }),
  );
}

function handleAddAllClick(): void {
  if (isDeletedProgramMode.value) return;
  const policiesToAdd = policies.value?.filter((p) => !p.added);

  if (!policiesToAdd) {
    return;
  }

  policiesToAdd.forEach((policy) => (policy.added = true));

  dispatchEvent(
    new CustomEvent(ADD_ALL_POLICIES, {
      detail: {
        policies: policiesToAdd,
      },
    }),
  );
}

function handleAddCustomPolicyClick(): void {
  if (isDeletedProgramMode.value) return;
  dispatchEvent(new CustomEvent(ADD_CUSTOM_POLICY));
}

function handleCustomPolicyClick(index: number): void {
  if (isDeletedProgramMode.value) return;
  if (!customPolicies.value) return;

  const customPolicy = customPolicies.value[index];
  customPolicy.added = !customPolicy.added;
  const eventType = customPolicy.added ? ADD_CUSTOM_POLICY : REMOVE_CUSTOM_POLICY;

  dispatchEvent(
    new CustomEvent(eventType, {
      detail: {
        policy: customPolicy,
      },
    }),
  );
}

function handleConfirmDeleteCustomPolicy(index: number) {
  if (isDeletedProgramMode.value) return;
  deleteCustomPolicyState.value = {
    show: true,
    index: index,
  };
}

function handleDeleteCustomPolicy(index?: number): void {
  if (index === undefined) return;
  if (!customPolicies.value) return;

  const policy = { ...customPolicies.value[index] };
  let customProgramPolicies = store?.state.towerCanvasState.customPolicies;
  if (customProgramPolicies) {
    customProgramPolicies = customProgramPolicies.filter((o) => o.id !== policy.id);
    store?.commit(StoreMutationType.SET_CUSTOM_POLICIES, customProgramPolicies);
  }

  dispatchEvent(
    new CustomEvent(REMOVE_CUSTOM_POLICY, {
      detail: {
        policy,
      },
    }),
  );

  deleteCustomPolicyState.value = {
    show: false,
    index: undefined,
  };
}

async function handleDeleteTowerVersion(tower: ProgramTower): Promise<void> {
  //delete the tower and navigate back to dashboard
  await towerService.deleteTower(tower.towerId);
  await router.replace(`/v2/tower`);
}

function handleEditTowerInfo(e: Event, tower: ProgramTower) {
  e.stopPropagation();
  store?.commit(StoreMutationType.EDIT_TOWER_INFO, {
    show: true,
    towerId: tower.towerId,
  });
}

async function handleSyncTowerLibraryPane(): Promise<void> {
  await syncCustomProgramPoliciesWithCurrentTower();
  await syncProgramPoliciesWithCurrentTower();
}

function handlePublishPolicy(e: CustomEvent<ProgramPolicy>): void {
  const policy = e.detail as ProgramPolicy;

  // update the policy in the program info
  if (policy) {
    const policyIndex = programInfo.value?.policies.findIndex(
      (p) => p.id === policy.id && p.policyLineId === policy.policyLineId,
    );
    if (policyIndex !== undefined && policyIndex !== -1) {
      programInfo.value?.policies.splice(policyIndex, 1, policy);
    }
  }
}

function handleUpdatePolicyChildren(e: CustomEvent<{ policyId: number; p_e_flag?: string }>) {
  programInfo.value?.policies.forEach((p) => {
    if (p.id === e.detail.policyId) {
      // Update the p_e_flag for the parent and all its children
      if (e.detail.p_e_flag) {
        p.p_e_flag = e.detail.p_e_flag;
      }
    }
  });
}

function closePanel(e: MouseEvent): void {
  e.stopPropagation();
  store?.commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, { show: false });
}

async function handlePublishClick(tower: ProgramTower, published: boolean): Promise<void> {
  if (tower.towerId == currentTowerInstance.value?.towerId) {
    currentTowerInstance.value.published = published;
  }

  tower.published = published;
  await towerService.saveTower({ tower });
}

function handleTogglePolicyHandler(): void {
  Commit(StoreMutationType.SET_TOWER_EDITOR_EXPANDED, !policyEditor.value.expanded);
}

// -------------------
// private functions
//--------------------
async function getProgramById(id: number): Promise<ProgramInfo> {
  return await programService.getProgramInfoById(id);
}

function isLabelVisible(option: keyof DisplayOptions): boolean {
  if (!displayOptions.value) return false;

  return displayOptions.value[option]?.enabled || false;
}

type ProgramPolicyItemKey = keyof ProgramPolicyItem;

async function syncProgramPoliciesWithCurrentTower(): Promise<void> {
  const policyRects: Map<string, ProgramPolicyItem> = new Map<string, ProgramPolicyItem>();
  try {
    const canvasObjects = JSON.parse(store?.state.towerCanvasState.currentTowerInstance?.towerObject ?? '').objects;

    (
      canvasObjects?.filter((o: fabric.PolicyRectOptions) => {
        return o.type === PolicyRectType;
      }) ?? []
    ).forEach((p: fabric.PolicyRectOptions) => {
      policyRects.set(p.policy.id.toString() + (p.policy.policyLineId ?? ''), p.policy);
    });
  } catch (e) {
    console.log('no policy rects to load');
  }

  const programPolicies = (programInfo.value?.policies ?? []).map((p) => {
    const policy = policyRects.get(p.id.toString() + (p.policyLineId ?? ''));
    const touched =
      policy?.touchedFields?.reduce(
        (touched, fieldName) => {
          const value = policy[fieldName as ProgramPolicyItemKey];
          if (value !== undefined) {
            touched[fieldName] = value;
          }
          return touched;
        },
        {} as Record<string, unknown>,
      ) ?? {};
    return {
      ...p,
      ...policy,
      added: policyRects.has(p.id.toString() + (p.policyLineId ?? '')),
      touchedFields: policy?.touchedFields,
      ...touched,
    };
  });

  // Group policy lines with their parent policy and sort parents by attachment point value
  const policyGroups = Array.from(
    programPolicies
      .reduce((acc, e) => {
        if (!acc.has(e.id)) {
          acc.set(e.id, [e]);
        } else {
          acc.get(e.id)?.push(e);
        }
        return acc;
      }, new Map<number, ProgramPolicyItem[]>())
      .values(),
  );

  policyGroups.forEach((policies) => {
    policies.sort((a, b) => {
      if (a.isChildPolicy && !b.isChildPolicy) {
        return 1;
      } else if (!a.isChildPolicy && b.isChildPolicy) {
        return -1;
      } else {
        return (a.attachmentPointValue ?? 0) - (b.attachmentPointValue ?? 0);
      }
    });
  });

  policyGroups.sort((a, b) => {
    return (a[0].attachmentPointValue ?? 0) - (b[0].attachmentPointValue ?? 0);
  });

  policies.value = policyGroups.flat();
}

async function syncCustomProgramPoliciesWithCurrentTower(): Promise<void> {
  // these are the custom policies currently on the canvas
  const customPolicyRects: Map<string, ProgramPolicyItem> = new Map<string, ProgramPolicyItem>();
  try {
    const canvasObjects = JSON.parse(store?.state.towerCanvasState.currentTowerInstance?.towerObject ?? '').objects;
    (
      canvasObjects?.filter((o: fabric.PolicyRectOptions) => {
        return o.type === CustomPolicyRectType;
      }) ?? []
    ).forEach((p: fabric.PolicyRectOptions) => {
      if (p.policy.id) {
        customPolicyRects.set(p.policy.id?.toString(), { ...p.policy, added: true });
      }
    });
  } catch (e) {
    console.log('no custom policy rects to load');
  }

  // check if any custom policies have been removed from the canvas
  const customProgramPolicyList = store?.state.towerCanvasState.customPolicies ?? [];
  let customProgramPolicies = customProgramPolicyList.reduce((acc, policy) => {
    acc.set(policy.id.toString(), policy);
    return acc;
  }, new Map<string, ProgramPolicyItem>());

  customProgramPolicies.forEach((policy, key) => {
    if (!customPolicyRects.has(key)) {
      policy.added = false;
    }
  });

  // merge stored custom policy list with the custom policies on the canvas
  customProgramPolicies = new Map([...customProgramPolicies, ...customPolicyRects]);

  customPolicies.value = Array.from(customProgramPolicies.values());
  store?.commit(StoreMutationType.SET_CUSTOM_POLICIES, customPolicies.value);
}

function dateFormatted(date: string): string {
  return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED);
}

function formatPolicyDisplay(policy: ProgramPolicyItem): string {
  const carrierName = policy.carrierShortName || policy.carrier;
  if (carrierName && policy.coverageType) {
    return `${carrierName} - ${policy.coverageType}`;
  }
  return carrierName || policy.coverageType || '';
}
</script>

<template>
  <div ref="sidebar" class="sidebar tw:opacity-95" v-show="show">
    <div class="header">
      <ul class="tabs tw:mt-2.5">
        <li
          v-for="tab in tabs"
          :key="tab.radioInputValue"
          class="tab tw:rounded-t tw:shadow-inner"
          :class="{ 'tab-selected': selectedTab === tab.radioInputValue }"
          @click="handleTabClick(tab.radioInputValue)">
          <label :title="tab.title" class="tab-label cursor-pointer">
            <input type="radio" name="tab" :aria-label="tab.ariaLabel" class="tab-input" :value="tab.radioInputValue" />
            {{ tab.labelText }}
          </label>
        </li>
      </ul>
      <label
        title="Close"
        class="sidebar-button tw:relative tw:mb-1.5 tw:flex tw:content-center tw:items-center tw:justify-center tw:shadow-inner tw:ease-in tw:hover:bg-gray-600/10">
        <input
          type="checkbox"
          name="editor-programs"
          aria-label="Programs Library"
          class="tw:absolute tw:invisible"
          @click="closePanel" />
        <X />
      </label>
    </div>
    <div class="body tw:h-full tw:min-w-[350px]" v-if="selectedTab === 'versions'">
      <div
        class="tw:flex tw:h-full tw:flex-col tw:justify-center tw:text-center tw:text-lg tw:font-medium"
        v-if="towerVersions.length === 0">
        <div>No tower versions saved yet.</div>
      </div>
      <div v-else class="tw:flex tw:w-full tw:flex-col tw:justify-center">
        <ul class="tw:list-none tw:p-0">
          <li
            v-for="towerVersion in towerVersions"
            :key="towerVersion.towerId"
            class="tw:m-2 tw:cursor-pointer tw:text-lg tw:font-normal"
            @click="handleTowerVersionClick(towerVersion)"
            :class="{ 'tw:opacity-60': towerVersion.loadError }"
            :title="towerVersion.loadError ? 'Unable to load version' : ''">
            <div
              class="tw:rounded-lg tw:border tw:border-solid tw:border-gray-500 tw:shadow-sm tw:transition-all tw:hover:bg-taupe-primary tw:hover:shadow-md"
              :class="isCurrentTowerVersion(towerVersion) ? 'tw:ml-2 tw:bg-taupe-primary' : 'tw:bg-taupe-secondary'">
              <div
                class="tw:flex tw:justify-between tw:rounded-t-lg tw:border-0 tw:border-b tw:border-solid tw:border-gray-400 tw:bg-taupe tw:p-1">
                <div class="tw:flex tw:items-center tw:pl-0.5">
                  <div v-if="towerVersion.published" class="tw:flex">
                    <CircleCheck class="tw:text-green-600"/>
                  </div>
                  <div class="tw:flex tw:items-center tw:gap-1 tw:pl-1 tw:text-xl">
                    <CircleAlert v-if="towerVersion.loadError" class="load-error"/>
                    {{ towerVersion.versionLabel ?? 'Initial Version' }}
                  </div>
                </div>
                <button
                  class="tw:mr-2 tw:ml-auto tw:rounded-lg tw:px-1 tw:text-sm tw:transition-all tw:duration-200 tw:hover:bg-tower-red"
                  @click="handleEditTowerInfo($event, towerVersion)">
                  <Edit class="tw:m-auto tw:scale-75 tw:text-gray-600" />
                </button>
                <button
                  class="tw:rounded-lg tw:px-1 tw:text-sm tw:transition-all tw:duration-200 tw:hover:bg-tower-red"
                  @click="handleDeleteTowerVersion(towerVersion)">
                  <X />
                </button>
              </div>
              <div class="tw:flex tw:flex-col tw:p-3 tw:pl-5 tw:text-gray-500">
                <div class="tw:mb-1 tw:text-sm tw:font-bold tw:text-gray-800">
                  {{ dateFormatted(towerVersion.updatedOn) }}
                </div>
                <div class="current-version tw:text-sm tw:italic" v-if="isCurrentTowerVersion(towerVersion)">
                  Current version
                </div>
                <div class="tw:mt-0.5 tw:text-gray-700">
                  <div v-if="(towerVersion.user.name ?? '').trim() !== ''" class="tw:text-sm">
                    {{ towerVersion.user.name }}
                  </div>
                  <div v-else class="tw:text-sm">{{ towerVersion.user.userEmail }}</div>
                </div>
                <v-switch
                  color="green"
                  hide-details
                  data-test="publish-switch"
                  class="tw:w-fit-content tw:mt-2"
                  v-model="towerVersion.published"
                  @click.stop="handlePublishClick(towerVersion, !towerVersion.published)"
                  title="Publishing will allow clients to view this tower">
                  <template v-slot:label>
                    <span class="tw:text-sm">Publish</span>
                  </template>
                </v-switch>
                <div class="tw:mt-2 tw:flex tw:text-xs">
                  <div>v{{ towerVersion.version }}</div>
                  <div class="tw:pl-1">Created {{ dateFormatted(towerVersion.createdOn) }}</div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div
      class="tw:flex tw:h-full tw:min-w-[350px] tw:flex-col tw:justify-between tw:overflow-y-auto"
      v-if="selectedTab === 'program'">
      <div class="tw:flex tw:h-full tw:flex-grow-2 tw:flex-col tw:overflow-y-auto">
        <div v-if="policyCards.length > 0" class="policies tw:p-2">
          <div class="tw:mb-2 tw:flex tw:place-items-center tw:content-center">
            <div
              class="tw:flex tw:items-center tw:hover:cursor-pointer"
              @click="isPoliciesCollapsed = !isPoliciesCollapsed">
              <ChevronRight v-if="isPoliciesCollapsed" class="tw:flex tw:cursor-pointer" />
              <ChevronDown v-else class="tw:flex tw:cursor-pointer" />
              <div class="tw:text-lg">Bound Policies</div>
            </div>
            <div
              v-if="!isDeletedProgramMode"
              class="tw:border-opacity-50 tw:ml-3 tw:cursor-pointer tw:rounded-lg tw:border tw:border-solid tw:border-black tw:p-1 tw:text-xs tw:transition-all tw:hover:bg-taupe-secondary"
              @click="handleAddAllClick">
              Add All Bound
            </div>
          </div>
          <div class="content" v-show="!isPoliciesCollapsed">
            <ul class="tw:m-0.5 tw:flex tw:flex-col tw:gap-2 tw:p-0">
              <li
                v-for="(policy, index) in policyCards"
                :key="'policy-id-' + policy.id + '' + (policy.policyLineId ? '-' + policy.policyLineId : '')"
                class="tw:pointer-events-none tw:flex tw:list-none tw:transition-all"
                @click="() => handlePolicyClick(Number(index))"
                :title="policy.description"
                :ref="(el) => policyCardRefs.set(policy.id, el)">
                <div class="tw:flex tw:items-center">
                  <Circle
                    :class="`tw:h-4 tw:w-4 tw:stroke-gray-500 tw:transition-all ${
                      policy.added ? 'tw:fill-green-400 tw:opacity-80' : 'tw:opacity-30'
                    }`"
                    class="tw:pointer-events-auto tw:cursor-pointer tw:pr-1" />
                </div>
                <div
                  class="tw:pointer-events-auto tw:relative tw:box-border tw:flex tw:cursor-pointer tw:list-none tw:flex-col tw:justify-between tw:rounded-md tw:border-1 tw:border-solid tw:border-tower-border tw:bg-taupe-secondary tw:transition-all"
                  :class="`${
                    policy.added && !policy.isChildPolicy
                      ? 'tw:ml-1.5 tw:bg-taupe-selected tw:shadow-none'
                      : 'tw:border-tower-border tw:shadow-md'
                  } ${policy.active ? 'tw:ring-2' : ''} ${policy.isChildPolicy ? 'tw:ml-6 tw:w-[92%]' : 'tw:w-full'} ${
                    policy.added && policy.isChildPolicy
                      ? 'tw:ml-8 tw:bg-taupe-selected tw:shadow-none'
                      : 'tw:shadow-md'
                  }`">
                  <svg
                    v-if="policy.isChildPolicy"
                    class="tw:pointer-events-none tw:absolute tw:-top-2 tw:-left-3 tw:z-0 tw:stroke-tower-border tw:transition-all"
                    :class="`${policy.added && policy.isChildPolicy ? 'tw:-ml-2' : ''}`">
                    <line x1="0" y1="0" x2="0" y2="55%" style="stroke-width: 3"></line>
                  </svg>
                  <div
                    class="tw:flex tw:flex-col tw:rounded-t-md tw:border-0 tw:border-b-1 tw:border-solid tw:border-gray-400 tw:bg-taupe-light tw:py-0.25 tw:pr-2 tw:pl-3">
                    <div class="tw:flex tw:justify-between tw:text-base">
                      <span class="tw:grow">{{
                        `${policy.carrierShortName || policy.carrier} - ${policy.coverageType}`
                      }}</span>
                    </div>
                  </div>
                  <div class="tw:flex tw:justify-between tw:px-4 tw:pt-0.5 tw:pb-1.5 tw:text-center">
                    <div class="tw:flex tw:flex-col tw:pl-1">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{
                        policy.coverageType === 'Work Comp' ? 'Statutory' : compactNumber(policy.aggregateLimit)
                      }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Aggregate</span>
                    </div>
                    <div class="tw:flex tw:flex-col">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{
                        policy.coverageType === 'Work Comp' ? 'Statutory' : compactNumber(policy.occurrenceLimit)
                      }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Occurrence</span>
                    </div>
                    <div v-if="policy.p_e_flag === 'Primary'" class="tw:flex tw:flex-col tw:pr-2">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{ compactNumber(policy.retention) }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Retention</span>
                    </div>
                    <div v-else class="tw:flex tw:flex-col tw:pr-1">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{
                        compactNumber(policy.attachmentPointValue)
                      }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Attachment</span>
                    </div>
                  </div>
                  <div
                    v-if="policy.touchedFields && policy.touchedFields.length > 0"
                    title="Out of sync with Policy"
                    class="tw:absolute tw:-top-2 tw:-left-2 tw:flex tw:rounded-3xl tw:bg-red-400 tw:p-1 tw:opacity-80"
                    >
                    <RefreshCwOff title="blah" :size="14" class="tw:text-white" />
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div class="custom-policies tw:p-2">
          <div class="tw:mb-2 tw:flex tw:place-items-center tw:content-center">
            <div
              class="tw:flex tw:items-center tw:hover:cursor-pointer"
              @click="isCustomPoliciesCollapsed = !isCustomPoliciesCollapsed">
              <ChevronRight v-if="isCustomPoliciesCollapsed" class="tw:cursor-pointer" />
              <ChevronDown v-else class="tw:cursor-pointer" />
              <div class="tw:text-lg">Custom Policies</div>
            </div>
            <div
              v-if="!isDeletedProgramMode"
              class="tw:border-opacity-50 tw:ml-3 tw:cursor-pointer tw:rounded-lg tw:border tw:border-solid tw:border-black tw:p-1 tw:text-xs tw:transition-all tw:hover:bg-taupe-secondary"
              @click="handleAddCustomPolicyClick">
              <Plus :size="16" />
            </div>
          </div>
          <div class="content" v-show="!isCustomPoliciesCollapsed">
            <ul class="tw:m-0.5 tw:flex tw:flex-col tw:gap-2 tw:p-0">
              <li
                v-for="(policy, index) in customPolicyCards"
                :key="policy.id"
                class="tw:pointer-events-none tw:flex tw:list-none tw:transition-all"
                :title="policy.description"
                :ref="(el) => customPolicyCardRefs.set(policy.id, el)"
                @click="() => handleCustomPolicyClick(Number(index))">
                <div class="tw:flex tw:items-center">
                  <Circle
                    :class="`tw:h-4 tw:w-4 tw:stroke-gray-500 tw:transition-all ${
                      policy.added ? 'tw:fill-green-400 tw:opacity-80' : 'tw:opacity-30'
                    }`"
                    class="tw:pointer-events-auto tw:cursor-pointer tw:pr-1" />
                </div>
                <div
                  class="tw:pointer-events-auto tw:relative tw:box-border tw:flex tw:w-full tw:cursor-pointer tw:flex-col tw:justify-between tw:rounded-md tw:border-1 tw:border-solid tw:border-tower-border tw:bg-taupe-secondary tw:transition-all"
                  :class="`${policy.added ? 'tw:ml-1.5 tw:bg-taupe-selected' : ''} ${policy.active ? 'tw:ring-2' : ''}`">
                  <div
                    class="tw:flex tw:flex-col tw:rounded-t-md tw:border-0 tw:border-b-1 tw:border-solid tw:border-gray-400 tw:bg-taupe-light tw:py-0.25 tw:pr-2 tw:pl-3">
                    <div class="tw:flex tw:justify-between tw:p-1 tw:text-base">
                      <span class="tw:grow">
                        {{ formatPolicyDisplay(policy) }}
                      </span>
                      <Trash2
                        v-if="!isDeletedProgramMode"
                        class="tw:h-5 tw:w-5 tw:cursor-pointer tw:transition-all tw:hover:text-red-500"
                        @click.stop="() => handleConfirmDeleteCustomPolicy(Number(index))" />
                    </div>
                  </div>
                  <div class="tw:flex tw:justify-between tw:px-4 tw:pt-0.5 tw:pb-1.5 tw:text-center">
                    <div class="tw:flex tw:flex-col tw:pl-1">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{
                        policy.coverageType === 'Work Comp' ? 'Statutory' : compactNumber(policy.aggregateLimit)
                      }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Aggregate</span>
                    </div>
                    <div class="tw:flex tw:flex-col">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{
                        policy.coverageType === 'Work Comp' ? 'Statutory' : compactNumber(policy.occurrenceLimit)
                      }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Occurrence</span>
                    </div>
                    <div v-if="policy.p_e_flag === 'Primary'" class="tw:flex tw:flex-col tw:pr-2">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{ compactNumber(policy.retention) }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Retention</span>
                    </div>
                    <div v-else class="tw:flex tw:flex-col tw:pr-1">
                      <span class="policy-item-value tw:pr-1 tw:text-lg">{{
                        compactNumber(policy.attachmentPointValue)
                      }}</span>
                      <span class="tw:text-gray-650 tw:self-center tw:text-xs">Attachment</span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-show="policyEditor.show" class="policy-editor tw:bg-taupe">
        <div
          class="policy-header tw:flex tw:items-center tw:rounded-s tw:bg-white tw:hover:cursor-pointer"
          @click="handleTogglePolicyHandler">
          <ChevronDown v-if="policyEditor.expanded" class="tw:scale-75" />
          <ChevronRight v-else class="tw:scale-75" />
          <div>POLICY EDITOR</div>
        </div>
        <policy-editor
          v-if="policyEditor.expanded"
          :policy="policyEditor.policy ?? ({} as ProgramPolicyItem)"
          :policy-type="policyEditor.policyType"
          :policy-rect-id="policyEditor.policyRectId" />
      </div>
    </div>
    <div class="body tw:h-full tw:min-w-[350px]" v-if="selectedTab === 'display-options'">
      <div>
        <div class="tw:flex tw:flex-row">
          <div class="label tw:self-center">Display Options</div>
          <div
            v-if="!isDeletedProgramMode"
            title="Edit"
            @click="editDisplayOptions"
            class="edit-button tw:mx-2 tw:cursor-pointer tw:rounded-lg tw:px-1.5 tw:ease-in tw:hover:bg-gray-600/10">
            <Pencil :size="16" />
          </div>
        </div>

        <div class="tw:text-md tw:flex tw:flex-col tw:gap-1 tw:px-2 tw:py-3">
          <div>
            <label
              for="carrier-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.CarrierLabel) }">
              <input
                type="checkbox"
                id="carrier-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.CarrierLabel"
                :checked="isLabelVisible(labels.CarrierLabel)" />

              <span v-if="displayOptions && displayOptions.carrier.customName" class="tw:grow">
                {{ displayOptions.carrier.customName }}<i> (Carrier)</i>
              </span>
              <span v-else class="tw:grow">Carrier</span>
            </label>
          </div>
          <div>
            <label
              for="am-best-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.AmBestLabel) }">
              <input
                type="checkbox"
                id="am-best-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.AmBestLabel"
                :checked="isLabelVisible(labels.AmBestLabel)" />

              <span v-if="displayOptions && displayOptions.amBest && displayOptions.amBest.customName" class="tw:grow">
                {{ displayOptions.amBest.customName }}<i> (AM Best)</i></span
              >
              <span v-else class="tw:grow"> AM Best</span>
            </label>
          </div>
          <div>
            <label
              for="aggregate-attach-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.AggregateLimitAndAttachPointLabel) }">
              <input
                type="checkbox"
                id="aggregate-attach-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.AggregateLimitAndAttachPointLabel"
                :checked="isLabelVisible(labels.AggregateLimitAndAttachPointLabel)" />

              <span v-if="displayOptions && displayOptions.aggregateLimit.customName" class="tw:grow">
                {{ displayOptions.aggregateLimit.customName }}<i> (Aggregate Limit)</i>
              </span>
              <span v-else class="tw:grow"> Aggregate Limit</span>
            </label>
          </div>
          <div>
            <label
              for="occurrence-limit-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.OccurrenceLimitAndAttachPointLabel) }">
              <input
                type="checkbox"
                id="occurrence-limit-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.OccurrenceLimitAndAttachPointLabel"
                :checked="isLabelVisible(labels.OccurrenceLimitAndAttachPointLabel)" />

              <span v-if="displayOptions && displayOptions.occurrenceLimit.customName" class="tw:grow">
                {{ displayOptions.occurrenceLimit.customName }}<i> (Occurrence Limit)</i>
              </span>
              <span v-else class="tw:grow"> Occurrence Limit</span>
            </label>
          </div>
          <div>
            <label
              for="aggregate-rate-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.AggregateRateOnLineLabel) }">
              <input
                type="checkbox"
                id="aggregate-rate-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.AggregateRateOnLineLabel"
                :checked="isLabelVisible(labels.AggregateRateOnLineLabel)" />

              <span v-if="displayOptions && displayOptions.aggregate.customName" class="tw:grow">
                {{ displayOptions.aggregate.customName }}<i> (Rate on Line (Aggregate))</i>
              </span>
              <span v-else class="tw:grow"> Rate on Line (Aggregate)</span>
            </label>
          </div>
          <div>
            <label
              for="occurrence-rate-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.OccurrenceRateOnLineLabel) }">
              <input
                type="checkbox"
                id="occurrence-rate-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.OccurrenceRateOnLineLabel"
                :checked="isLabelVisible(labels.OccurrenceRateOnLineLabel)" />

              <span v-if="displayOptions && displayOptions.occurrence.customName" class="tw:grow">
                {{ displayOptions.occurrence.customName }}<i> (Rate on Line (Occurrence))</i>
              </span>
              <span v-else class="tw:grow"> Rate on Line (Occurrence)</span>
            </label>
          </div>
          <div>
            <label
              for="premium-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.PremiumLabel) }">
              <input
                type="checkbox"
                id="premium-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.PremiumLabel"
                :checked="isLabelVisible(labels.PremiumLabel)" />

              <span v-if="displayOptions && displayOptions.premium.customName" class="tw:grow">
                {{ displayOptions.premium.customName }}<i> (Premium)</i>
              </span>
              <span v-else class="tw:grow"> Premium</span>
            </label>
          </div>
          <div>
            <label
              for="coverage-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.CoverageLabel) }">
              <input
                type="checkbox"
                id="coverage-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.CoverageLabel"
                :checked="isLabelVisible(labels.CoverageLabel)" />

              <span class="tw:grow"> Coverage Type</span>
            </label>
          </div>
          <div>
            <label
              for="quotaShare-percentage-label"
              class="tw:flex tw:items-center tw:gap-2 tw:py-2 tw:transition-all"
              :class="{ 'tw:font-bold': isLabelVisible(labels.QuotaSharePercent) }">
              <input
                type="checkbox"
                id="quotaShare-percentage-label"
                class="tw:toggle tw:toggle-primary tw:toggle-sm"
                @click="updateDisplayOption"
                :value="labels.QuotaSharePercent"
                :checked="isLabelVisible(labels.QuotaSharePercent)" />

              <span class="tw:grow"> Quota Share as Percentage</span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <AppModal
      :show="deleteCustomPolicyState.show"
      title="Delete Custom Policy"
      :handle-cancel="() => (deleteCustomPolicyState.show = false)"
      :handle-action="() => handleDeleteCustomPolicy(deleteCustomPolicyState.index)"
      action-button-label="Delete">
      <template #content>
        <div class="tw:text-center">
          <p class="tw:text-lg tw:text-gray-600">Are you sure you want to delete this custom policy?</p>
        </div>
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.sidebar {
  position: absolute;
  right: 0;
  top: 0;
  background-color: var(--tower-v2-alt-bg-color);
  box-shadow: var(--sidebar-shadow);
  display: flex;
  flex-direction: column;
  max-width: 23rem;
  min-width: 23rem;
  height: 100%;
  overflow-y: auto;
  z-index: 5;
  border-left: 1px solid var(--tower-v2-border-color);
}

.sidebar .header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  border-bottom: 1px solid var(--tower-v2-border-color);
  padding: 0.5rem 0.5rem 0 0.2rem;
}

.sidebar .header .tabs {
  display: flex;
  padding: 0;
}

.sidebar .header .tabs .tab {
  display: flex;
  list-style-type: none;
  align-items: center;
  padding: 0 0.35rem 0 0.35rem;
  border: 1px solid var(--tower-v2-border-color);
  border-bottom: none;
}

.sidebar .header .tabs .tab .tab-label {
  font-size: 1rem;
  cursor: pointer;
}

.sidebar .header .tabs .tab .tab-input {
  position: fixed;
  visibility: hidden;
}

.tab-selected {
  background-color: var(--tower-v2-tool-selected-color);
  opacity: 1;
  transition: background-color 0.15s;
}

.sidebar .body {
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  width: 100%;
}

.sidebar-button {
  cursor: pointer;
  pointer-events: all;
  background-color: var(--tower-v2-alt-bg-color);
  border: 1px solid var(--tower-v2-border-color);
  border-radius: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
}

.current-version {
  color: #b85058;
}

.policy-item-value {
  height: 1.4rem;
}

.load-error {
  color: var(--tower-v2-primary-color);
}
</style>
